// Calling-hours windows (CRTC). Windows are [start, end): the end instant itself is NOT allowed.
import { zonedTimeToUtc } from "../torontoTime";
import { addDays } from "./toronto";
import { zoneParts } from "./zone";

export type CallingHours = {
  weekday: readonly [string, string];
  weekend: readonly [string, string];
};

export const DEFAULT_CALLING_HOURS: CallingHours = Object.freeze({
  weekday: ["09:00", "21:30"],
  weekend: ["10:00", "18:00"],
}) as CallingHours;

const minutesOf = (hhmm: string): number => {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m || +m[1] > 23 || +m[2] > 59)
    throw new RangeError(`invalid time: ${hhmm}`);
  return +m[1] * 60 + +m[2];
};

const windowFor = (weekday: number, hours: CallingHours) => {
  const window = weekday >= 6 ? hours.weekend : hours.weekday;
  if (minutesOf(window[0]) >= minutesOf(window[1]))
    throw new RangeError(`invalid calling window: ${window[0]}-${window[1]}`);
  return window;
};

/**
 * Dialer holidays (owner decision 2026-10-14): no call on Dec 25 or Jan 1, every year, by rule. The date is the
 * LEAD's local date, like the calling windows. Deliberately independent of gavion.holidays (the business-day and
 * email list an admin edits): removing Christmas from that list never unblocks calls. SQL twin:
 * private.dialer_holiday (supabase/schemas/47_gavion_holiday_block.sql).
 */
const isHolidayMonthDay = (month: number, day: number): boolean =>
  (month === 12 && day === 25) || (month === 1 && day === 1);

/** True when `at` falls on Dec 25 or Jan 1 in `tz`. */
export function isDialerHoliday(at: Date, tz: string): boolean {
  const p = zoneParts(at, tz);
  return isHolidayMonthDay(p.month, p.day);
}

/** True when `at` (in `tz`) is within [start, end) of that day's window; Dec 25 and Jan 1 are closed all day. */
export function withinCallingHours(
  at: Date,
  tz: string,
  hours: CallingHours,
): boolean {
  const p = zoneParts(at, tz);
  const [start, end] = windowFor(p.weekday, hours);
  if (isHolidayMonthDay(p.month, p.day)) return false;
  const seconds = p.hour * 3600 + p.minute * 60 + p.second;
  return seconds >= minutesOf(start) * 60 && seconds < minutesOf(end) * 60;
}

/** First instant >= `at` inside calling hours (skips Dec 25 and Jan 1). */
export function nextCallingWindow(
  at: Date,
  tz: string,
  hours: CallingHours,
): Date {
  if (withinCallingHours(at, tz, hours)) return at;
  const p = zoneParts(at, tz);
  const today = `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
  for (let i = 0; i <= 7; i += 1) {
    const day = addDays(today, i);
    if (isHolidayMonthDay(+day.slice(5, 7), +day.slice(8, 10))) continue;
    const dow = ((new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7) + 1;
    const candidate = zonedTimeToUtc(day, windowFor(dow, hours)[0], tz);
    if (candidate.getTime() > at.getTime()) return candidate;
  }
  throw new RangeError("no calling window found");
}
